// hooks/useStripePromise.ts
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getPublishableKey } from '../redux/payment/action';
import { paymentStateSelector, publishableKeySelector } from '../pages/EsSettings/selector';

const stripePromiseByKey = new Map<string, Promise<Stripe | null>>();

function getSharedStripePromise(publishableKey: string): Promise<Stripe | null> {
    const cached = stripePromiseByKey.get(publishableKey);
    if (cached) {
        return cached;
    }
    const promise = loadStripe(publishableKey);
    stripePromiseByKey.set(publishableKey, promise);
    return promise;
}

export type UseStripePromiseOptions = {
    /** When false, do not fetch Stripe keys (avoids overwriting Paystack on billing). */
    enabled?: boolean;
};

/**
 * Loads Stripe.js. Prefer `publishableKeyOverride` from GET /payment/rail
 * (`stripe_publishable_key`) so EU buyers hit the EU account.
 *
 * Uses the Stripe-specific cached key so Paystack/Flutterwave keys in Redux
 * are not passed to `loadStripe` or clobbered by background fetches.
 */
export const useStripePromise = (
    publishableKeyOverride?: string | null,
    options?: UseStripePromiseOptions,
): Promise<Stripe | null> | null => {
    const dispatch = useDispatch();
    const enabled = options?.enabled !== false;
    const { publishableKey, publishableKeySource } = useSelector(publishableKeySelector);
    const payment = useSelector(paymentStateSelector);
    const cachedStripeKey = payment.publishableKeys?.stripe ?? '';
    // Legacy / tests may set publishableKey without a source; treat that as Stripe.
    const sharedIsStripe = !publishableKeySource || publishableKeySource === 'stripe' ? publishableKey : '';
    const stripeKey = (publishableKeyOverride || cachedStripeKey || sharedIsStripe || '').trim();

    useEffect(() => {
        if (!enabled) return;
        if (publishableKeyOverride) return;
        if (stripeKey) return;
        dispatch(getPublishableKey({ source: 'stripe' }));
    }, [dispatch, enabled, publishableKeyOverride, stripeKey]);

    const stripePromise = useMemo(() => {
        if (!enabled) return null;
        if (stripeKey) {
            return getSharedStripePromise(stripeKey);
        }
        return null;
    }, [enabled, stripeKey]);

    return stripePromise;
};

/** Clears module-level Stripe cache between tests. */
export const clearStripePromiseCacheForTests = (): void => {
    stripePromiseByKey.clear();
};
